import {memo, useState, useEffect} from 'react';
import CustomLinkWithArrow from '@/_application/core/components/commons/custom-link-with-arrow';
import CustomSwiper from '@/_application/core/components/ui/custom-swiper';
import {SwiperSlide} from '@/_application/core/components/ui/slider';
import Timer from '@/_application/core/components/ui/timer';
import {FlashSaleIcon} from '@/_application/icons/flash-sale';
import {FlashSaleInfo} from '@/_application/modules/flash-sales/components/home-page/flash-sale-info';
import {ProductCard} from '@/_application/modules/products/components/cards/product-card';
import {Routes} from '@/config/routes';
import {useTranslation} from 'next-i18next';
import {FlashSale, FlashSaleProduct, FlashSaleProductData} from '@/__generated__/__types__';
import {Product} from '@/_application/modules/products/model/product';

const flashSalesSliderBreakpoints = {
    320: {
        slidesPerView: 2,
    },
    480: {
        slidesPerView: 3,
    },
    640: {
        slidesPerView: 3,
    },
    768: {
        slidesPerView: 5, // QA require 5
    },
    1024: {
        slidesPerView: 5, // QA require 5
    },
    1280: {
        slidesPerView: 5,
    },
};

/**
 * Check if flash sale is currently valid based on date range
 */
const checkFlashSaleValid = (flashSaleInfo: FlashSale | null | undefined): boolean => {
    if (!flashSaleInfo) {
        return false;
    }
    const currentDate = new Date();
    const saleStartDate = new Date(flashSaleInfo.date_from!);
    const saleEndDate = new Date(flashSaleInfo.date_to!);
    return currentDate.getTime() >= saleStartDate.getTime() && currentDate.getTime() <= saleEndDate.getTime();
};

export interface FlashSalesSliderProps {
    flashSaleData?: {
        items: FlashSaleProduct[];
        flashSaleInfo: FlashSale | null;
    };
}

function FlashSalesSliderInner({flashSaleData}: FlashSalesSliderProps) {
    const {t} = useTranslation('common');
    const [isTimeUp, setIsTimeUp] = useState(false);
    // Only validate on client to avoid hydration mismatch
    const [isValidOnClient, setIsValidOnClient] = useState(true);

    const items = flashSaleData?.items ?? [];
    const flashSaleInfo = flashSaleData?.flashSaleInfo;

    // Validate flash sale only on client side after mount
    useEffect(() => {
        setIsValidOnClient(checkFlashSaleValid(flashSaleInfo));
    }, [flashSaleInfo]);

    const handleTimeUp = () => {
        setIsTimeUp(true);
    };

    // Server: render if has items (let client decide validity)
    // Client: hide if time is up or invalid
    const hasItems = items.length > 0 && flashSaleInfo;

    // Don't render if no data at all
    if (!hasItems) {
        return null;
    }

    // On client, hide if invalid or time is up
    if (typeof window !== 'undefined' && (!isValidOnClient || isTimeUp)) {
        return null;
    }

    return (
        <div className="mt-12 flex min-h-[32.5rem] w-[100vw] items-center justify-center bg-gradient-to-tr from-orange-500 to-amber-300 bg-fixed">
            <div className="relative w-[698px] pb-7 lg:w-[950px] xl:w-[1196px]" style={{background: 'inherit'}}>
                <div className="absolute -top-8 left-0 rounded-xl p-5" style={{background: 'inherit'}}>
                    <FlashSaleIcon />
                </div>

                <div className="absolute left-[160px] top-4">
                    {flashSaleInfo?.date_to && (
                        <Timer
                            dateTo={flashSaleInfo?.date_to}
                            onTimeUp={handleTimeUp}
                            className="items-center gap-1 text-base font-bold text-white"
                            elementClassName="rounded-[6px] bg-KPZ_turquoise px-2 py-[1px]"
                        />
                    )}
                </div>

                <div className="absolute right-0 top-4">
                    <CustomLinkWithArrow text={t('text-view-all', 'Xem tất cả')} href={Routes.flashSale} />
                </div>

                <div className="pt-12">
                    <CustomSwiper
                        className="mt-5 !grid h-[441px] !grid-cols-5 place-items-center"
                        hasNavigation
                        slidesPerView={4}
                        slidePerGroup={5}
                        spaceBetween={30}
                        buttonPrevClassName="-left-6 !h-11 !w-11"
                        buttonNextClassName="-right-6 !h-11 !w-11"
                        breakpoints={flashSalesSliderBreakpoints}
                        key={`flash-sale-slider`}>
                        {items.map((flashSaleProduct: FlashSaleProduct) => (
                            <SwiperSlide key={flashSaleProduct?.product?.sku}>
                                <ProductCard
                                    product={flashSaleProduct.product as Product}
                                    additionalData={{
                                        flashSale: flashSaleProduct.sales_data,
                                    }}
                                    afterCardContent={
                                        <FlashSaleInfo data={flashSaleProduct.sales_data as FlashSaleProductData} />
                                    }
                                />
                            </SwiperSlide>
                        ))}
                    </CustomSwiper>
                </div>
            </div>
        </div>
    );
}

export const FlashSalesSlider = memo(FlashSalesSliderInner);
